/* ==========================================================================
   FORG3D — Add-On Picker adapter (DOM + cart mutations).

   Thin UI layer over the pure engine (forg3d-addon-rules.js). Serves two cart
   surfaces, each scoped so their links/counts never cross-contaminate:

   • Cart PAGE (context 'page') — picker is server-rendered by
     forg3d-addon-picker.liquid; decisions come from the Liquid cart-state blob
     (data-forg3d-addon-state); mutations reload the page.

   • Cart DRAWER (context 'drawer') — the drawer rebuilds its body in JS from
     /cart.js (no metafields), so the picker is injected here by decorate(),
     fed the Liquid catalog (data-forg3d-addon-catalog); mutations refresh the
     drawer in place via window.FORG3DCart.refresh().

   Contract (data hooks): [data-forg3d-addon-toggle|-add|-skip|-remove|-pending].
   ========================================================================== */
(function () {
  'use strict';

  var Rules = window.FORG3DAddonRules;
  var DISMISS_KEY = 'forg3d:addon-dismissed';
  var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var lastDrawerDecisions = {};

  /* ------------------------------------------------------------- utilities */

  function dismissed() {
    try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY)) || {}; }
    catch (e) { return {}; }
  }
  function dismiss(key) {
    var d = dismissed();
    d[key] = 1;
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(d)); } catch (e) {}
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function money(cents) {
    // Single source of truth: assets/forg3d-money.js (presentment-currency aware).
    // The old inline fallback here hardcoded '$' and was the ONLY reachable
    // path on this store, so EUR carts rendered as "$149.85" (Chatwoot 214).
    //
    // `compact`, not `format`: both cart-page twins of these two callsites
    // (forg3d-addon-option.liquid, forg3d-addon-child.liquid) render through
    // money_without_trailing_zeros, and the drawer must not read "$12.00"
    // beside the page's "$12".
    if (window.FORG3DMoney) { return window.FORG3DMoney.compact(cents); }
    var iso = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    var amount = Number(cents) / 100;
    return (iso ? iso + ' ' : '') + (amount % 1 === 0 ? String(amount) : amount.toFixed(2));
  }
  function parseJSONHook(sel) {
    var el = document.querySelector(sel);
    if (!el) { return null; }
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  // The stable half of a Shopify line key. Delegates to the engine so the
  // pairing rule has exactly one definition; the fallback covers rules.js
  // failing to load.
  function refOf(key) {
    if (Rules && typeof Rules.lineRef === 'function') { return Rules.lineRef(key); }
    return typeof key === 'string' ? key.split(':')[0] : '';
  }
  function evaluate(state) {
    if (!state || !Rules || typeof Rules.evaluateCart !== 'function') { return {}; }
    var map = {};
    Rules.evaluateCart(state).forEach(function (d) { map[d.lineKey] = d; });
    return map;
  }

  /* ---------------------------------------------- shared painting (scoped) */

  function paintSurface(root, map) {
    if (!root) { return; }
    var d = dismissed();
    var pending = 0;
    var noun = '';

    root.querySelectorAll('[data-forg3d-addon-toggle]').forEach(function (link) {
      var key = link.dataset.lineKey;
      var dec = map[key];
      if (dec && dec.satisfied) {
        link.dataset.state = 'mounted';
        link.textContent = link.dataset.labelAdded || 'ADDED';
      } else if (dec && dec.suppressed) {
        link.dataset.state = 'mounted';
        link.textContent = link.dataset.labelSuppressed || link.dataset.labelAdded || 'ADDED';
      } else if (d[key]) {
        link.dataset.state = 'skipped';
        link.textContent = link.dataset.labelSkipped || 'SKIPPED';
      } else {
        link.dataset.state = 'open';
        link.textContent = link.dataset.labelOpen || link.textContent;
        pending += 1;
        if (!noun && link.dataset.pendingNoun) { noun = link.dataset.pendingNoun; }
      }
    });

    if (!noun) { noun = 'mount'; }
    var target = root.querySelector('[data-forg3d-addon-pending]');
    if (target) {
      target.hidden = pending === 0;
      target.textContent = pending === 1
        ? '1 item still needs a ' + noun
        : pending + ' items still need a ' + noun;
    }
  }

  function pageRoot() { return document.querySelector('[data-forg3d-cart]'); }
  function drawerRoot() { return document.querySelector('[data-forg3d-cart-drawer]'); }

  function paintPage() {
    paintSurface(pageRoot(), evaluate(parseJSONHook('[data-forg3d-addon-state]')));
  }

  /* --------------------------------------------- drawer markup + decorate */

  function optionHtml(o, lineKey, qty, role, recLabel) {
    var badge = o.recommended ? '<span class="forg3d-addon__badge">' + esc(recLabel || 'Recommended') + '</span>' : '';
    var desc = o.blurb ? '<span class="forg3d-addon__opt-desc">' + esc(o.blurb) + '</span>' : '';
    // Mirrors forg3d-addon-option.liquid: show the count whenever more than one
    // is being added, so a "+$30" on a $15 plate explains itself.
    var mult = qty > 1 ? ' <span class="forg3d-addon__opt-qty">×' + qty + '</span>' : '';
    return '<button type="button" class="forg3d-addon__opt" data-forg3d-addon-add'
      + ' data-variant-id="' + o.variantId + '" data-parent-key="' + esc(lineKey) + '"'
      + ' data-qty="' + qty + '" data-addon-role="' + esc(role) + '">'
      + '<span class="forg3d-addon__opt-text"><span class="forg3d-addon__opt-head">'
      + '<span class="forg3d-addon__opt-name">' + esc(o.name) + mult + '</span>' + badge + '</span>' + desc
      + '<span class="forg3d-addon__opt-sku">' + esc(o.sku) + '</span></span>'
      + '<span class="forg3d-addon__opt-price">+' + money(o.price * qty) + '</span></button>';
  }
  function linkHtml(cat, lineKey) {
    var L = cat.labels;
    return '<button type="button" class="forg3d-addon__link" data-forg3d-addon-toggle'
      + ' data-line-key="' + esc(lineKey) + '" data-group-key="' + esc(cat.group) + '" data-context="drawer"'
      + ' data-pending-noun="' + esc(cat.pendingNoun || 'add-on') + '"'
      + ' data-label-open="' + esc(L.open) + '" data-label-added="' + esc(L.added) + '"'
      + ' data-label-suppressed="' + esc(L.suppressed) + '" data-label-skipped="' + esc(L.skipped) + '"'
      + ' aria-expanded="false" aria-controls="forg3d-addon-panel-' + esc(lineKey) + '">' + esc(L.open) + '</button>';
  }
  function panelHtml(cat, lineKey, qty) {
    var L = cat.labels;
    var role = (cat.pendingNoun || 'add-on').toUpperCase();
    var opts = (cat.options || []).map(function (o) { return optionHtml(o, lineKey, qty, role, L.rec); }).join('');
    var sub = L.sub ? '<p class="forg3d-addon__sub">' + esc(L.sub) + '</p>' : '';
    var alt = (L.altLabel && L.altUrl) ? '<a class="forg3d-addon__alt" href="' + esc(L.altUrl) + '">' + esc(L.altLabel) + '</a>' : '';
    return '<div class="forg3d-addon__panel" id="forg3d-addon-panel-' + esc(lineKey) + '"'
      + ' data-forg3d-addon-panel data-line-key="' + esc(lineKey) + '" hidden>'
      + '<div class="forg3d-addon__prompt">' + esc(L.prompt) + '</div>' + sub
      + '<div class="forg3d-addon__options" role="group" aria-label="' + esc(L.prompt) + '">' + opts + alt
      + '<button type="button" class="forg3d-addon__skip" data-forg3d-addon-skip data-line-key="' + esc(lineKey) + '">'
      + esc(L.none) + '</button></div></div>';
  }
  function childHtml(item, parentItem) {
    var role = (item.properties && item.properties._addon_role) || 'ADD-ON';
    var title = parentItem.product_title || '';
    var m = title.match(/\(([^)]+)\)/);
    var code = m ? m[1].toUpperCase() : title.slice(0, 3).toUpperCase();
    return '<div class="forg3d-addon__child" data-line-key="' + esc(item.key) + '" data-addon-child>'
      + '<div class="forg3d-addon__child-eyebrow">' + esc(role) + ' FOR ' + esc(code) + '</div>'
      + '<div class="forg3d-addon__child-row"><a href="' + esc(item.url) + '" class="forg3d-addon__child-name">'
      + esc(item.product_title) + '</a><span class="forg3d-addon__child-total">' + money(item.final_line_price) + '</span></div>'
      + '<div class="forg3d-addon__child-row"><span class="forg3d-addon__child-meta">'
      + esc(item.sku || '') + ' · ×' + item.quantity + '</span>'
      + '<button type="button" class="forg3d-addon__child-remove" data-forg3d-addon-remove data-line-key="'
      + esc(item.key) + '">Remove</button></div></div>';
  }

  function drawerState(cart, catalog) {
    var lines = cart.items.map(function (it) {
      var cat = catalog[it.product_id] || {};
      var addonFor = it.properties && it.properties._addon_for;
      return {
        key: it.key,
        group: cat.group || null,
        isAddon: !!addonFor,
        addonFor: addonFor || null,
        suppressesGroups: cat.suppressesGroups || []
      };
    });
    var groups = {};
    cart.items.forEach(function (it) {
      var cat = catalog[it.product_id];
      if (cat && cat.group) { groups[cat.group] = { mode: cat.mode }; }
    });
    return { lines: lines, groups: groups };
  }

  /* Inject picker link + panel + child lines into the freshly-rendered drawer
     body. Called by forg3d-cart-drawer.js after each render(cart). */
  function decorate(cart, container) {
    if (!container || !cart || !cart.items) { return; }
    var catalog = parseJSONHook('[data-forg3d-addon-catalog]') || {};
    var byKey = {};
    var childrenOf = {};
    cart.items.forEach(function (it) {
      byKey[it.key] = it;
      var pf = it.properties && it.properties._addon_for;
      // Key by the variant-id half: the parent's hash rotates when a bundle
      // discount attaches. See sameLine() in forg3d-addon-rules.js.
      if (pf) { var ref = refOf(pf); (childrenOf[ref] = childrenOf[ref] || []).push(it); }
    });

    container.querySelectorAll('.forg3d-cart-drawer__item[data-line-key]').forEach(function (article) {
      var key = article.getAttribute('data-line-key');
      var item = byKey[key];
      if (!item) { return; }
      var cat = catalog[item.product_id];

      // No renderable options → no trigger. Without this the drawer offers a
      // link that opens an empty panel (the cart page gates on opt_count in
      // Liquid; this is the drawer's equivalent).
      if (cat && cat.group && cat.labels && cat.options && cat.options.length) {
        var cutlist = article.querySelector('.forg3d-cart-drawer__cutlist');
        if (cutlist && !article.querySelector('[data-forg3d-addon-toggle]')) {
          var row = document.createElement('div');
          row.className = 'forg3d-cart-drawer__cutlistrow';
          cutlist.parentNode.insertBefore(row, cutlist);
          row.appendChild(cutlist);
          row.insertAdjacentHTML('beforeend', linkHtml(cat, key));
        }
        var next = article.nextElementSibling;
        if (!next || !next.matches('[data-forg3d-addon-panel]')) {
          // qtyPer: add-ons needed per unit of the trigger (TRK takes two
          // plates), scaled by the line quantity. Mirrors add_qty in
          // forg3d-addon-picker.liquid so both surfaces add the same count.
          article.insertAdjacentHTML('afterend', panelHtml(cat, key, item.quantity * (cat.qtyPer || 1)));
        }
      }

      var kids = childrenOf[refOf(key)] || [];
      var anchor = article.nextElementSibling && article.nextElementSibling.matches('[data-forg3d-addon-panel]')
        ? article.nextElementSibling
        : article;
      // Insert in reverse so sequential afterend keeps source order.
      for (var i = kids.length - 1; i >= 0; i--) {
        anchor.insertAdjacentHTML('afterend', childHtml(kids[i], item));
      }
    });

    lastDrawerDecisions = evaluate(drawerState(cart, catalog));
    paintSurface(container.closest('[data-forg3d-cart-drawer]') || drawerRoot(), lastDrawerDecisions);
  }

  window.FORG3DAddonDrawer = { decorate: decorate };

  /* ------------------------------------------------------------- mutations */

  function inDrawer(el) { return !!(el && el.closest && el.closest('[data-forg3d-cart-drawer]')); }

  function afterMutation(el) {
    if (inDrawer(el) && window.FORG3DCart && typeof window.FORG3DCart.refresh === 'function') {
      window.FORG3DCart.refresh();
    } else {
      window.location.reload();
    }
  }

  function addAddon(btn) {
    if (btn.getAttribute('aria-busy') === 'true') { return; }
    btn.setAttribute('aria-busy', 'true');
    fetch(routeRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id: Number(btn.dataset.variantId),
          quantity: Number(btn.dataset.qty || 1),
          properties: { _addon_for: btn.dataset.parentKey, _addon_role: btn.dataset.addonRole || 'ADD-ON' }
        }]
      })
    })
      .then(function (r) { if (!r.ok) { throw new Error('add failed'); } return r.json(); })
      .then(function () { afterMutation(btn); })
      .catch(function () { btn.removeAttribute('aria-busy'); });
  }

  function removeLine(key, el) {
    fetch(routeRoot + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then(function (r) { return r.json(); })
      .then(function () { afterMutation(el); })
      .catch(function () {});
  }

  /* --------------------------------------------------------------- events */

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-forg3d-addon-toggle]');
    if (toggle) {
      if (toggle.dataset.state === 'mounted') { return; }
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) { return; }
      var open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        var firstOpt = panel.querySelector('[data-forg3d-addon-add]');
        if (firstOpt) { firstOpt.focus(); }
      }
      return;
    }

    var add = e.target.closest('[data-forg3d-addon-add]');
    if (add) { addAddon(add); return; }

    var skip = e.target.closest('[data-forg3d-addon-skip]');
    if (skip) {
      dismiss(skip.dataset.lineKey);
      var p = skip.closest('[data-forg3d-addon-panel]');
      if (p) { p.hidden = true; }
      if (inDrawer(skip)) { paintSurface(drawerRoot(), lastDrawerDecisions); }
      else { paintPage(); }
      return;
    }

    var rm = e.target.closest('[data-forg3d-addon-remove]');
    if (rm) { removeLine(rm.dataset.lineKey, rm); }
  });

  document.addEventListener('forg3d:cart-updated', paintPage);
  document.addEventListener('forg3d:cart:changed', paintPage);
  if (document.readyState !== 'loading') { paintPage(); }
  else { document.addEventListener('DOMContentLoaded', paintPage); }
})();
